import {
  tablesDB,
  DATABASE_ID,
  STORE_TABLE_ID,
  ORDERS_TABLE_ID,
  REQUESTS_TABLE_ID,
  USERS_TABLE_ID,
  Query,
} from '../lib/appwrite';

/**
 * Get store owner metrics: stores owned with real order/revenue data per store.
 * ownerId in stores matches user.uid or user.$id
 * @param {string} userId - user.$id
 * @param {string} [userUid] - user.uid (optional, for matching ownerId)
 */
export async function getSellerMetrics(userId, userUid) {
  const matchIds = [userId, userUid].filter(Boolean).map(String);
  if (!matchIds.length) return { stores: [], totalStores: 0, totalOrders: 0, totalRevenue: 0 };

  const [storesRes, ordersRes] = await Promise.all([
    tablesDB.listRows(DATABASE_ID, STORE_TABLE_ID, [Query.limit(500)]),
    tablesDB.listRows(DATABASE_ID, ORDERS_TABLE_ID, [Query.limit(10000)]),
  ]);

  const stores = (storesRes.rows || []).filter((s) =>
    matchIds.some((id) => String(s.ownerId || '') === id)
  );
  const orders = ordersRes.rows || [];

  const byOrderId = {};
  orders.forEach((o) => {
    const key = o.orderId || o.$id;
    if (!byOrderId[key]) {
      byOrderId[key] = { storeId: o.storeId, totalAmount: 0 };
    }
    byOrderId[key].totalAmount += parseFloat(o.totalAmount) || 0;
  });

  const storeStats = {};
  Object.values(byOrderId).forEach(({ storeId, totalAmount }) => {
    if (!storeId) return;
    if (!storeStats[storeId]) {
      storeStats[storeId] = { orderCount: 0, revenue: 0 };
    }
    storeStats[storeId].orderCount += 1;
    storeStats[storeId].revenue += totalAmount;
  });

  const storeIds = new Set(stores.map((s) => s.$id));
  let totalOrders = 0;
  let totalRevenue = 0;
  const storesWithMetrics = stores.map((s) => {
    const stats = storeStats[s.$id] || { orderCount: 0, revenue: 0 };
    totalOrders += stats.orderCount;
    totalRevenue += stats.revenue;
    return {
      storeId: s.$id,
      storeName: s.storeName || s.$id,
      orderCount: stats.orderCount,
      revenue: stats.revenue,
      isActive: s.isActive,
    };
  });

  const ordersForStores = orders.filter((o) => storeIds.has(o.storeId));
  const customerIds = [...new Set(ordersForStores.map((o) => o.customerId).filter(Boolean))];
  const byCustomer = {};
  ordersForStores.forEach((o) => {
    const cid = o.customerId;
    if (!cid) return;
    const key = o.orderId || o.$id;
    if (!byCustomer[cid]) byCustomer[cid] = { orderIds: new Set(), totalSpent: 0 };
    byCustomer[cid].orderIds.add(key);
    byCustomer[cid].totalSpent += parseFloat(o.totalAmount) || 0;
  });
  const customers = await Promise.all(
    customerIds.slice(0, 100).map(async (cid) => {
      try {
        const u = await tablesDB.getRow(DATABASE_ID, USERS_TABLE_ID, cid);
        const stats = byCustomer[cid] || { orderIds: new Set(), totalSpent: 0 };
        return {
          customerId: cid,
          name: [u.fname, u.lname].filter(Boolean).join(' ') || u.email || cid,
          email: u.email,
          orderCount: stats.orderIds.size,
          totalSpent: stats.totalSpent,
        };
      } catch {
        const stats = byCustomer[cid] || { orderIds: new Set(), totalSpent: 0 };
        return { customerId: cid, name: 'Unknown', email: null, orderCount: stats.orderIds.size, totalSpent: stats.totalSpent };
      }
    })
  );
  customers.sort((a, b) => (b.orderCount || 0) - (a.orderCount || 0));

  return {
    stores: storesWithMetrics,
    totalStores: stores.length,
    totalOrders,
    totalRevenue,
    customers,
  };
}

/**
 * Get customer metrics: orders, stores visited, top products, delivery options.
 */
export async function getCustomerMetrics(userId, userUid) {
  const ids = [...new Set([userId, userUid].filter(Boolean).map(String))];
  if (!ids.length) {
    return { orderCount: 0, totalSpent: 0, byStatus: {}, topStores: [], topProducts: [], deliveryUsage: {} };
  }

  const results = await Promise.all(
    ids.map((id) =>
      tablesDB.listRows(DATABASE_ID, ORDERS_TABLE_ID, [
        Query.equal('customerId', id),
        Query.limit(2000),
      ])
    )
  );
  const rows = results.flatMap((r) => r.rows || []);
  const byOrder = {};
  const statusCount = {};
  const byStore = {};
  const byProduct = {};
  const byDelivery = {};

  rows.forEach((r) => {
    const key = r.orderId || r.$id;
    if (!byOrder[key]) {
      byOrder[key] = { total: 0, status: r.status || 'unknown', storeId: r.storeId, deliveryMethod: r.deliveryMethod || 'unknown' };
    }
    byOrder[key].total += parseFloat(r.totalAmount) || 0;
    byOrder[key].status = r.status || byOrder[key].status;
    if (r.storeId) {
      if (!byStore[r.storeId]) byStore[r.storeId] = { orderIds: new Set(), totalSpent: 0 };
      byStore[r.storeId].orderIds.add(key);
      byStore[r.storeId].totalSpent += parseFloat(r.totalAmount) || 0;
    }
    const prodKey = r.productName || r.productId || 'unknown';
    if (prodKey !== 'unknown') {
      if (!byProduct[prodKey]) byProduct[prodKey] = { quantity: 0, orderIds: new Set() };
      byProduct[prodKey].quantity += parseInt(r.quantity, 10) || 0;
      byProduct[prodKey].orderIds.add(key);
    }
    const dm = (r.deliveryMethod || 'unknown').trim() || 'unknown';
    if (!byDelivery[dm]) byDelivery[dm] = new Set();
    byDelivery[dm].add(key);
  });

  let totalSpent = 0;
  Object.values(byOrder).forEach(({ total, status }) => {
    totalSpent += total;
    const s = (status || 'unknown').toLowerCase();
    statusCount[s] = (statusCount[s] || 0) + 1;
  });

  const storeIds = [...new Set(Object.keys(byStore).filter(Boolean))];
  const storeRows = storeIds.length > 0
    ? await Promise.all(storeIds.slice(0, 50).map((id) =>
        tablesDB.getRow(DATABASE_ID, STORE_TABLE_ID, id).catch(() => ({ $id: id, storeName: id }))
      ))
    : [];
  const storeMap = {};
  storeRows.forEach((s) => { storeMap[s.$id] = s.storeName || s.$id; });

  const topStores = Object.entries(byStore)
    .map(([storeId, data]) => ({
      storeId,
      storeName: storeMap[storeId] || storeId,
      orderCount: data.orderIds.size,
      totalSpent: data.totalSpent,
    }))
    .sort((a, b) => b.orderCount - a.orderCount)
    .slice(0, 10);

  const topProducts = Object.entries(byProduct)
    .map(([name, data]) => ({ productName: name, quantity: data.quantity, orderCount: data.orderIds.size }))
    .sort((a, b) => b.quantity - a.quantity)
    .slice(0, 10);

  const deliveryUsage = Object.fromEntries(
    Object.entries(byDelivery).map(([k, set]) => [k, set.size])
  );

  return {
    orderCount: Object.keys(byOrder).length,
    totalSpent,
    byStatus: statusCount,
    topStores,
    topProducts,
    deliveryUsage,
  };
}

/**
 * Get driver metrics: real delivery data from requests where driverId = user.$id or user.uid.
 * @param {string} userId - user.$id
 * @param {string} [userUid] - user.uid
 */
export async function getDriverMetrics(userId, userUid) {
  const matchIds = [userId, userUid].filter(Boolean).map(String);
  if (!matchIds.length) return { total: 0, completed: 0, pending: 0, inProgress: 0 };

  const res = await tablesDB.listRows(DATABASE_ID, REQUESTS_TABLE_ID, [
    Query.limit(5000),
  ]);
  const rows = (res.rows || []).filter((r) =>
    matchIds.some((id) => String(r.driverId || '') === id)
  );

  let completed = 0;
  let pending = 0;
  let inProgress = 0;
  rows.forEach((r) => {
    const status = (r.status || '').toLowerCase();
    if (status === 'completed' || r.isComplete) {
      completed += 1;
    } else if (status === 'pending' || status === 'confirm' || !r.isTaken) {
      pending += 1;
    } else {
      inProgress += 1;
    }
  });

  return {
    total: rows.length,
    completed,
    pending,
    inProgress,
  };
}

/**
 * Get role-specific metrics for a user. Calls the right fetcher based on role.
 */
export async function getUserRoleMetrics(user, role) {
  const r = (role || user?.roles || user?.role || '').toLowerCase();
  const uid = user?.$id || user?.uid;

  if (r === 'seller') {
    return getSellerMetrics(user?.$id, user?.uid);
  }
  if (r === 'customer') {
    return getCustomerMetrics(user?.$id, user?.uid);
  }
  if (r === 'driver') {
    return getDriverMetrics(user?.$id, user?.uid);
  }
  return null;
}
